import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowUpRight, ArrowLeft, ShieldCheck, Users, Globe2, Loader2 } from "lucide-react";
import useSEO from "@/lib/useSEO";

export default function Featured() {
  useSEO({
    title: "Featured Fee-Only Financial Advisors · Centennial",
    description: "Independent, fee-only financial advisors who can help you implement your retirement plan. Each listed advisor paid $99/yr; Centennial does not vet them — do your own diligence.",
    canonicalPath: "/featured",
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/advisor/listings")
      .then((r) => setData(r.data))
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="featured-page" className="min-h-screen bg-[#F9F8F6] text-[#1C2B23]">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/70 border-b border-white/40">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm hover:text-[#2B5339]">
            <ArrowLeft size={16} /> Back
          </Link>
          <div className="font-display tracking-tight text-lg">
            Centennial<span className="text-[#C26747]">.</span>
          </div>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 sm:px-12 py-14 lg:py-20">
        <div className="text-[11px] tracking-[0.22em] uppercase text-[#5C6B62] font-semibold inline-flex items-center gap-2">
          <Users size={12} /> featured advisors
        </div>
        <h1 className="font-display mt-2 text-4xl sm:text-5xl tracking-tight font-medium max-w-3xl">
          Real humans you can hire to <span className="italic text-[#2B5339]">plan your money.</span>
        </h1>
        <p className="mt-4 text-[#5C6B62] max-w-2xl">
          These fee-only advisors paid $99/yr to be listed here. Centennial doesn't vet them — do your
          own research. Each link is theirs to claim.
        </p>

        <div className="mt-12">
          {loading && (
            <div className="text-[#5C6B62] text-sm inline-flex items-center gap-2">
              <Loader2 className="animate-spin" size={14} /> loading…
            </div>
          )}

          {!loading && (!data || data.count === 0) && (
            <div data-testid="featured-empty" className="bg-white border border-dashed border-[#E5E2DC] rounded-2xl p-10 text-center">
              <Globe2 className="mx-auto text-[#5C6B62]" size={28} />
              <h2 className="font-display text-2xl mt-4">No advisors listed yet.</h2>
              <p className="mt-2 text-[#5C6B62] max-w-md mx-auto text-sm">
                Be the first. If you're a fee-only advisor or financial coach, get listed for $99/yr.
              </p>
              <Link
                to="/pricing"
                data-testid="cta-list-advisor"
                className="mt-5 inline-flex items-center gap-2 bg-[#2B5339] text-white hover:bg-[#1E3B28] rounded-full px-5 py-2.5 text-sm"
              >
                <ShieldCheck size={14} /> Get listed — $99/yr
              </Link>
            </div>
          )}

          {data?.advisors?.length > 0 && (
            <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {data.advisors.map((a) => (
                <li
                  key={a.listing_id}
                  data-testid={`advisor-card-${a.listing_id}`}
                  className="bg-white border border-[#E5E2DC] rounded-2xl p-6 flex flex-col"
                >
                  <div className="flex items-start justify-between gap-3">
                    {a.logo_url ? (
                      <img
                        src={a.logo_url}
                        alt={a.display_name}
                        className="w-12 h-12 rounded-full object-cover border border-[#E5E2DC]"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    ) : (
                      <span className="w-12 h-12 rounded-full bg-[#2B5339] text-white inline-flex items-center justify-center font-display text-lg">
                        {a.display_name?.[0] ?? "·"}
                      </span>
                    )}
                    <a
                      href={a.website_url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-[#5C6B62] hover:text-[#2B5339]"
                    >
                      <ArrowUpRight size={18} />
                    </a>
                  </div>
                  <div className="font-display text-xl text-[#1C2B23] mt-3">{a.display_name}</div>
                  {a.location && (
                    <div className="text-[11px] tracking-[0.16em] uppercase text-[#5C6B62] font-semibold mt-1">{a.location}</div>
                  )}
                  {a.tagline && (
                    <div className="text-sm text-[#2B5339] mt-2 font-medium">{a.tagline}</div>
                  )}
                  {a.bio && (
                    <p className="text-sm text-[#5C6B62] mt-3 leading-relaxed line-clamp-5">{a.bio}</p>
                  )}
                  <a
                    href={a.website_url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="mt-5 inline-flex items-center justify-center gap-2 bg-transparent border border-[#2B5339] text-[#2B5339] hover:bg-[#F0EFEA] rounded-full py-2 text-sm"
                  >
                    Visit advisor site <ArrowUpRight size={13} />
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
